// noinspection ES6PreferShortImport,JSUnusedGlobalSymbols

import {
    DocSnapTyped,
    DocumentData,
    DocumentReference,
    fieldValueArrayRemove,
    fieldValueArrayUnion,
    Firestore,
    newFieldValueIncrement,
    Query,
    QuerySnapshot,
    QuerySnapTyped,
    Timestamp,
    UpdateData
} from '../extern/FirebaseTypes'
import {
    ApiKeyData,
    ApiKeyMode,
    CompanyData,
    MetadataEquipmentData,
    AccessCodeData,
    User,
    UserData,
    UserRole,
    ZipCodeData
} from './Types'
import { ApiEventData } from './ApiEvents'
import { Equipment, EquipmentData, EquipmentDataUnion } from "./EquipmentTypes"

const accessCodesCollection = 'accessCodes';
const apiKeysCollection = 'apiKeys'
const apiEventsCollection = 'apiEvents'
const equipmentCollection = 'equipment'
const metadataCollection = 'metadata'
const metadataEquipmentDocRef = 'equipment'

// This must be initialized via initializeDatabase()
let db: Firestore

export interface UserQueryParams {
    companyId?: string
    isTemp?: boolean
    tempAccessCodeId?: string
    orderByCreatedTimestampDescending?: boolean
}

export interface UpdateCompanyParams {
    newName?: string
    newEmail?: string
}

export interface UpdateUserParams {
    newFirstName?: string
    newLastName?: string
    newName?: string
    newEmail?: string
    newRole?: UserRole
}

export interface UpdateAccessCodeParams {
    newName?: string
    newAccessEndTimestamp?: Timestamp
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface AccessCodeQueryParams {
    orderByCreatedTimestampDescending?: boolean
}

export interface CompanyQueryParams {
    stripeCustomerId?: string
}

export interface UpdateApiKeyParams {
    newIsActive?: boolean
    newName?: string
}

export interface ApiKeyQueryParams {
    isActive?: boolean
    companyId?: string
    mode?: ApiKeyMode
    orderByCreatedTimestampDescending?: boolean
}

export interface ApiEventQueryParams {
    createdTimestampGreaterThanOrEqualTo?: Timestamp
    createdTimestampLessThan?: Timestamp
    partnerCompanyId?: string
    mode?: ApiKeyMode
}

export interface EquipmentQueryParams {
    equipmentType?: string
}

export interface UpdateMetadataEquipmentParams {
    newFormatVersion?: number
    incrementChangeVersion?: boolean
}

/**
 * Called to initialize any global variables in this file
 */
export function initializeDatabase(_db: Firestore) {
    db = _db
}

//-------------------------------------------------------------------------------------------------
// Company Functions
//-------------------------------------------------------------------------------------------------

export function createCompanyId(): string {
    return db.collection('companies').doc().id
}

export function getCompanyDocRef(id: string): DocumentReference {
    return db.doc(`companies/${id}`);
}

export async function getCompanyData(id: string): Promise<CompanyData|undefined> {
    const doc = await getCompanyDocRef(id).get()
    return doc.data() as CompanyData
}

export async function setCompanyData(id: string, data: CompanyData): Promise<void> {
    await getCompanyDocRef(id).set(data)
}

export async function udpateCompanyData(id: string, params: UpdateCompanyParams): Promise<void> {
    const updateData: UpdateData = {}
    if (params.newName) {
        updateData.name = params.newName
        updateData.nameLower = params.newName.toLowerCase()
    }
    if (params.newEmail)
        updateData.email = params.newEmail
    await getCompanyDocRef(id).update(updateData)
}

export function streamCompanyData(id: string, onNext: (snapshot: DocSnapTyped<CompanyData>) => void): VoidFunction {
    return streamDoc<CompanyData>(getCompanyDocRef(id), onNext)
}

export async function queryCompanies(params?: CompanyQueryParams): Promise<QuerySnapTyped<CompanyData>> {
    const query = createCompaniesQuery(params)
    const snapshot = await query.get()
    return QuerySnapTyped.fromSnapshot(snapshot, data => data as CompanyData)
}

function createCompaniesQuery(params?: CompanyQueryParams): Query {
    let query: Query = db.collection('companies')

    if (params) {
        if (params.stripeCustomerId)
            query = query.where('stripeCustomerId', '==', params.stripeCustomerId)
    }

    return query
}

//-------------------------------------------------------------------------------------------------
// User Functions
//-------------------------------------------------------------------------------------------------

export function createUserId(): string {
    return db.collection('users').doc().id
}

export function getUserDocRef(id: string): DocumentReference {
    return db.doc(`users/${id}`);
}

export async function getUserData(id: string): Promise<UserData|undefined> {
    const doc = await getUserDocRef(id).get()
    return doc.data() as UserData
}

export async function setUserData(id: string, data: UserData): Promise<void> {
    await getUserDocRef(id).set(data)
}

export async function udpateUserData(id: string, params: UpdateUserParams): Promise<void> {
    const updateData: UpdateData = {}
    if (params.newFirstName) {
        updateData.firstName = params.newFirstName
        updateData.firstNameLower = params.newFirstName.toLowerCase()
    }
    if (params.newLastName) {
        updateData.lastName = params.newLastName
        updateData.lastNameLower = params.newLastName.toLowerCase()
    }
    if (params.newName) {
        updateData.name = params.newName
        updateData.nameLower = params.newName.toLowerCase()
    }
    if (params.newEmail)
        updateData.email = params.newEmail
    if (params.newRole)
        updateData.role = params.newRole
    await getUserDocRef(id).update(updateData)
}

export async function deleteUser(id: string): Promise<void> {
    await getUserDocRef(id).delete()
}

export async function addUserSubscription(id: string, subscriptionId: string): Promise<void> {
    await getUserDocRef(id).update({
        stripeSubscriptionIds: fieldValueArrayUnion(subscriptionId)
    })
}

export async function removeUserSubscription(id: string, subscriptionId: string): Promise<void> {
    await getUserDocRef(id).update({
        stripeSubscriptionIds: fieldValueArrayRemove(subscriptionId)
    })
}

export function streamUserData(id: string, onNext: (snapshot: DocSnapTyped<UserData>) => void): VoidFunction {
    return streamDoc<UserData>(getUserDocRef(id), onNext)
}

export function streamUsers(params: UserQueryParams,
                            onNext: (snapshot: QuerySnapshot) => void,
                            onError?: (error: Error) => void): VoidFunction {
    const query = createUserQuery(params)
    return query.onSnapshot(onNext, onError)
}

export async function queryUsersRaw(params?: UserQueryParams): Promise<QuerySnapTyped<UserData>> {
    const query = createUserQuery(params)
    const snapshot = await query.get()
    return QuerySnapTyped.fromSnapshot(snapshot, data => data as UserData)
}

export async function queryUsers(params?: UserQueryParams): Promise<User[]> {
    const snapshot = await queryUsersRaw(params)
    return snapshot.docs.map((doc) => {
        return {
            id: doc.id,
            data: doc.data!!,
        }
    })
}

function createUserQuery(params?: UserQueryParams): Query {
    let query: Query = db.collection('users')

    if (params) {
        if (params.companyId)
            query = query.where('companyId', '==', params.companyId)
        if (params.isTemp !== undefined)
            query = query.where('isTemp', '==', params.isTemp)
        if (params.tempAccessCodeId !== undefined)
            query = query.where('tempAccessCodeId', '==', params.tempAccessCodeId)
        if (params.orderByCreatedTimestampDescending)
            query = query.orderBy('createdTimestamp', 'desc')
    }

    return query
}

//-------------------------------------------------------------------------------------------------
// Access Code Functions
//-------------------------------------------------------------------------------------------------

export function getAccessCodeDocRef(id: string): DocumentReference {
    return db.doc(`${accessCodesCollection}/${id}`);
}

export async function getAccessCodeData(id: string): Promise<AccessCodeData|undefined> {
    const doc = await getAccessCodeDocRef(id).get()
    return doc.data() as AccessCodeData
}

export async function setAccessCodeData(id: string, data: AccessCodeData): Promise<void> {
    await getAccessCodeDocRef(id).set(data)
}

export async function updateAccessCodeData(id: string, params: UpdateAccessCodeParams): Promise<void> {
    const updateData: UpdateData = {}
    if (params.newName) {
        updateData.name = params.newName
        updateData.nameLower = params.newName.toLowerCase()
    }
    if (params.newAccessEndTimestamp)
        updateData.accessEndTimestamp = params.newAccessEndTimestamp
    await getAccessCodeDocRef(id).update(updateData)
}

export async function deleteAccessCode(id: string): Promise<void> {
    await getAccessCodeDocRef(id).delete()
}

export async function queryAccessCodes(params?: AccessCodeQueryParams): Promise<QuerySnapTyped<AccessCodeData>> {
    const query = createAccessCodeQuery(params)
    const snapshot = await query.get()
    return QuerySnapTyped.fromSnapshot(snapshot, data => data as AccessCodeData)
}

export function streamAccessCodes(params: AccessCodeQueryParams,
                                  onNext: (snapshot: QuerySnapshot) => void,
                                  onError?: (error: Error) => void): VoidFunction {
    const query = createAccessCodeQuery(params)
    return query.onSnapshot(onNext, onError)
}


function createAccessCodeQuery(params?: AccessCodeQueryParams): Query {
    let query: Query = db.collection(accessCodesCollection)

    if (params) {
        if (params.orderByCreatedTimestampDescending)
            query = query.orderBy('createdTimestamp', 'desc')
    }

    return query
}

//-------------------------------------------------------------------------------------------------
// Zip Code Functions
//-------------------------------------------------------------------------------------------------

export function getZipCodeDocRef(id: string): DocumentReference {
    return db.doc(`zipCodes/${id}`);
}

export async function getZipCodeData(id: string): Promise<ZipCodeData|undefined> {
    const doc = await getZipCodeDocRef(id).get()
    return doc.data() as ZipCodeData
}

export async function setZipCodeData(id: string, data: ZipCodeData): Promise<void> {
    await getZipCodeDocRef(id).set(data)
}

//-------------------------------------------------------------------------------------------------
// Stripe Event Functions
//-------------------------------------------------------------------------------------------------

export function getStripeEventDocRef(id: string): DocumentReference {
    return db.doc(`stripeEvents/${id}`)
}

export async function containsStripeEventData(id: string): Promise<boolean> {
    const doc = await getStripeEventDocRef(id).get()
    return doc.exists
}

export async function setStripeEventData(id: string, data: DocumentData): Promise<void> {
    await getStripeEventDocRef(id).set(data)
}

//-------------------------------------------------------------------------------------------------
// API Key Functions
//-------------------------------------------------------------------------------------------------

export function createApiKeyId(mode: ApiKeyMode): string {
    // combine two IDs to make the key longer
    const id1 = db.collection(apiKeysCollection).doc().id
    const id2 = db.collection(apiKeysCollection).doc().id
    const prefix = (mode === ApiKeyMode.PROD) ? 'prod' : 'dev'
    return `${prefix}_${id1}${id2}`
}

export function getApiKeyDocRef(id: string): DocumentReference {
    return db.doc(`${apiKeysCollection}/${id}`);
}

export async function getApiKeyData(id: string): Promise<ApiKeyData|undefined> {
    const doc = await getApiKeyDocRef(id).get()
    return doc.data() as ApiKeyData
}

export async function setApiKeyData(id: string, data: ApiKeyData): Promise<void> {
    await getApiKeyDocRef(id).set(data)
}

export async function udpateApiKeyData(id: string, params: UpdateApiKeyParams): Promise<void> {
    const updateData: UpdateData = {}
    if (params.newIsActive !== undefined)
        updateData.isActive = params.newIsActive
    if (params.newName)
        updateData.name = params.newName
    await getApiKeyDocRef(id).update(updateData)
}

export async function queryApiKeys(params?: ApiKeyQueryParams): Promise<QuerySnapTyped<ApiKeyData>> {
    const query = createApiKeysQuery(params)
    const snapshot = await query.get()
    return QuerySnapTyped.fromSnapshot(snapshot, data => data as ApiKeyData)
}

export function streamApiKeys(params: ApiKeyQueryParams,
                              onNext: (snapshot: QuerySnapshot) => void,
                              onError?: (error: Error) => void): VoidFunction {
    const query = createApiKeysQuery(params)
    return query.onSnapshot(onNext, onError)
}

function createApiKeysQuery(params?: ApiKeyQueryParams): Query {
    let query: Query = db.collection(apiKeysCollection)

    if (params) {
        if (params.isActive !== undefined)
            query = query.where('isActive', '==', params.isActive)
        if (params.companyId)
            query = query.where('companyId', '==', params.companyId)
        if (params.mode)
            query = query.where('mode', '==', params.mode)
        if (params.orderByCreatedTimestampDescending)
            query = query.orderBy('createdTimestamp', 'desc')
    }

    return query
}

//-------------------------------------------------------------------------------------------------
// API Event Functions
//-------------------------------------------------------------------------------------------------

export async function addApiEvent(data: ApiEventData): Promise<string> {
    const docRef = await db.collection('apiEvents').add(data)
    return docRef.id
}

export async function queryApiEvents(params?: ApiEventQueryParams): Promise<QuerySnapTyped<ApiEventData>> {
    const query = createApiEventsQuery(params)
    const snapshot = await query.get()
    return QuerySnapTyped.fromSnapshot(snapshot, data => data as ApiEventData)
}

function createApiEventsQuery(params?: ApiEventQueryParams): Query {
    let query: Query = db.collection(apiEventsCollection)

    if (params) {
        if (params.createdTimestampGreaterThanOrEqualTo)
            query = query.where('createdTimestamp', '>=', params.createdTimestampGreaterThanOrEqualTo)
        if (params.createdTimestampLessThan)
            query = query.where('createdTimestamp', '<', params.createdTimestampLessThan)
        if (params.partnerCompanyId !== undefined)
            query = query.where('partner.companyId', '==', params.partnerCompanyId)
        if (params.mode !== undefined)
            query = query.where('partner.apiKeyMode', '==', params.mode)
    }

    return query
}

//-------------------------------------------------------------------------------------------------
// Equipment Functions
//-------------------------------------------------------------------------------------------------

export function createEquipmentId(): string {
    return db.collection(equipmentCollection).doc().id
}

export function getEquipmentDocRef(id: string): DocumentReference {
    return db.doc(`${equipmentCollection}/${id}`);
}

export async function getEquipmentData(id: string): Promise<EquipmentData|undefined> {
    const doc = await getEquipmentDocRef(id).get()
    return doc.data() as EquipmentData
}

export async function setEquipmentData(id: string, data: EquipmentDataUnion): Promise<void> {
    await getEquipmentDocRef(id).set(data)
}

export function streamEquipmentData(id: string, onNext: (snapshot: DocSnapTyped<EquipmentData>) => void): VoidFunction {
    return streamDoc<EquipmentData>(getEquipmentDocRef(id), onNext)
}

export async function queryEquipmentRaw(params?: EquipmentQueryParams): Promise<QuerySnapTyped<EquipmentData>> {
    const query = createEquipmentQuery(params)
    const snapshot = await query.get()
    return QuerySnapTyped.fromSnapshot(snapshot, data => data as EquipmentData)
}

export async function queryEquipment(params?: EquipmentQueryParams): Promise<Equipment[]> {
    const snapshot = await queryEquipmentRaw(params)
    return snapshot.docs.map((doc) => {
        return {
            id: doc.id,
            data: doc.data!!,
        }
    })
}

function createEquipmentQuery(params?: EquipmentQueryParams): Query {
    let query: Query = db.collection(equipmentCollection)

    if (params) {
        if (params.equipmentType)
            query = query.where('equipmentType', '==', params.equipmentType)
    }

    return query
}

//-------------------------------------------------------------------------------------------------
// Metadata Functions
//-------------------------------------------------------------------------------------------------

export function getMetadataEquipmentDocRef(): DocumentReference {
    return db.doc(`${metadataCollection}/${metadataEquipmentDocRef}`);
}

export async function getMetadataEquipment(): Promise<MetadataEquipmentData|undefined> {
    const doc = await getMetadataEquipmentDocRef().get()
    return doc.data() as MetadataEquipmentData
}

export async function setMetadataEquipmentData(data: MetadataEquipmentData): Promise<void> {
    await getMetadataEquipmentDocRef().set(data)
}

export function streamMetadataEquipment(onNext: (snapshot: DocSnapTyped<MetadataEquipmentData>) => void): VoidFunction {
    return streamDoc<MetadataEquipmentData>(getMetadataEquipmentDocRef(), onNext)
}

export async function updateMetadataEquipmentData(params: UpdateMetadataEquipmentParams): Promise<void> {
    const updateData: UpdateData = {}
    if (params.newFormatVersion) {
        updateData.formatVersion = params.newFormatVersion
    }
    if (params.incrementChangeVersion) {
        updateData.changeVersion = newFieldValueIncrement(1)
    }
    await getMetadataEquipmentDocRef().update(updateData)
}

//-------------------------------------------------------------------------------------------------
// Helper Functions
//-------------------------------------------------------------------------------------------------

function streamDoc<T>(docRef: DocumentReference, onNext: (snapshot: DocSnapTyped<T>) => void): VoidFunction {
    return docRef.onSnapshot(doc =>
        onNext(new DocSnapTyped(doc, data => data as T))
    )
}